import { LatLngBounds } from 'leaflet'
import { Store } from 'vuex'

declare global {
  interface Window {
    dataLayer?: any[]
  }
}

export type PlaceStatisticBody = {
  place?: number | null
  type: string
  event?: number | null
  transmission_type?: number
}

export type PopularSearchFilters = {
  teams: Array<{ id: number, name: string, count: number, map_url?: string }>
  leagues: Array<{ id: number, name: string, count: number, map_url?: string }>
  days?: number
}

/** True when Consent Mode / CMP granted analytics_storage. */
export function hasAnalyticsConsent (): boolean {
  try {
    const dataLayer = window.dataLayer
    if (Array.isArray(dataLayer)) {
      for (let i = dataLayer.length - 1; i >= 0; i--) {
        const item = dataLayer[i]
        if (!Array.isArray(item) || item[0] !== 'consent') {
          continue
        }
        const action = item[1]
        const state = item[2]
        if ((action === 'update' || action === 'default') && state && typeof state === 'object') {
          if ('analytics_storage' in state) {
            return state.analytics_storage === 'granted'
          }
        }
      }
    }
  } catch (_) {
    /* ignore */
  }
  return false
}

export function mapCenterFromBounds (bounds: LatLngBounds | null | undefined): { lat: number, lon: number } | null {
  if (!bounds) {
    return null
  }
  try {
    const center = bounds.getCenter()
    return { lat: center.lat, lon: center.lng }
  } catch (_) {
    return null
  }
}

export function buildPlaceStatisticPayload (
  store: Store<any>,
  body: PlaceStatisticBody
) {
  const mapCenter = mapCenterFromBounds(store.getters.mapBounds)
  const payload: Record<string, unknown> = {
    type: body.type,
    transmission_type: body.transmission_type ?? 0,
    analytics_consent: hasAnalyticsConsent(),
    ...(mapCenter ? { map_center: mapCenter } : {})
  }
  if (body.place != null) {
    payload.place = body.place
  }
  if (body.event != null) {
    payload.event = body.event
  }
  return payload
}

export function postPlaceStatistic (
  store: Store<any>,
  body: PlaceStatisticBody
) {
  const requestURL = new URL(`${location.origin}/watchout/api/place_statistics`)
  return fetch(requestURL.toString(), {
    headers: {
      'Content-Type': 'application/json'
    },
    method: 'POST',
    body: JSON.stringify(buildPlaceStatisticPayload(store, body)),
    credentials: 'same-origin'
  })
}

export function fetchPopularPlaceStatistics (language?: string): Promise<PopularSearchFilters> {
  const requestURL = new URL(`${location.origin}/watchout/api/place_statistics/popular`)
  if (language) {
    requestURL.searchParams.set('language', language)
  }
  return fetch(requestURL.toString(), { credentials: 'same-origin' })
    .then(response => response.json())
    .then(data => ({
      teams: Array.isArray(data && data.teams) ? data.teams : [],
      leagues: Array.isArray(data && data.leagues) ? data.leagues : [],
      days: data && data.days
    }))
}
